import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppContext, ACTIONS } from '../context/AppContext';
import CallQueueView from './CallQueueView';
import ConversationView from './ConversationView';
import { ReactComponent as GetTalkyLogo } from '../Get Talky Icon Logo SVG.svg';
import TranscriptPanel from './TranscriptPanel';

const POPUP_MIN_WIDTH = 320;
const POPUP_MIN_HEIGHT = 400;
const POPUP_MAX_WIDTH = 600;
const POPUP_MAX_HEIGHT = 800;
const BUBBLE_SIZE = 64; // 16 * 4 px
const TRANSCRIPT_WIDTH = 400;
const WIDGET_PADDING = 16;

// Loading component for when data is being fetched
const LoadingView = () => (
  <div className="h-full flex flex-col items-center justify-center bg-gray-50 text-gray-600">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4"></div>
    <p className="text-sm">Loading interactions...</p>
  </div>
);

// Error component for when API calls fail
const ErrorView = ({ error, onRetry }) => (
  <div className="h-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 p-4">
    <div className="text-red-500 text-4xl mb-4">⚠️</div>
    <p className="text-sm text-center mb-4">Failed to load data:</p>
    <p className="text-xs text-red-500 text-center mb-4">{error}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm"
    >
      Retry
    </button>
  </div>
);

const AssistantWidget = () => {
  const { state, dispatch } = useAppContext();
  const [bubblePosition, setBubblePosition] = useState(state.widgetState.position);
  const [isBubbleDragging, setIsBubbleDragging] = useState(false);
  const [bubbleDragOffset, setBubbleDragOffset] = useState({ x: 0, y: 0 });
  const [popoutPosition, setPopoutPosition] = useState({ x: 0, y: 0 });
  const [popoutSize, setPopoutSize] = useState({ width: 384, height: 600 });
  const [isPopoutDragging, setIsPopoutDragging] = useState(false);
  const [popoutDragOffset, setPopoutDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStartData, setResizeStartData] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const { widgetState, currentInteraction, transcriptState, isLoading, error } = state;
  const { isOpen, view } = widgetState;
  const pendingInteractions = state.interactions.filter(interaction => interaction.status === 'pending');
  const bubbleRef = useRef(null);
  const popoutRef = useRef(null);
  const transcriptPanelRef = useRef(null);
  const mouseDownInfo = useRef({ x: 0, y: 0, time: 0 });

  // Retry loading data on error
  const handleRetry = () => {
    window.location.reload(); // Simple retry - reload the app
  };

  const handleBubbleMouseDown = (e) => {
    if (e.button !== 0) return;
    mouseDownInfo.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    
    const rect = bubbleRef.current.getBoundingClientRect();
    setBubbleDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsBubbleDragging(true);
    e.preventDefault();
  };

  const handleBubbleMouseMove = (e) => {
    if (!isBubbleDragging) return;
    
    const newX = e.clientX - bubbleDragOffset.x;
    const newY = e.clientY - bubbleDragOffset.y;
    
    const maxX = window.innerWidth - BUBBLE_SIZE;
    const maxY = window.innerHeight - BUBBLE_SIZE;
    
    setBubblePosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleBubbleMouseUp = (e) => {
    if (!isBubbleDragging) return;
    
    const timeDiff = Date.now() - mouseDownInfo.current.time;
    const distanceDiff = Math.sqrt(
      Math.pow(e.clientX - mouseDownInfo.current.x, 2) +
      Math.pow(e.clientY - mouseDownInfo.current.y, 2)
    );
    
    setIsBubbleDragging(false);
    
    if (timeDiff < 200 && distanceDiff < 5) {
      dispatch({ type: ACTIONS.TOGGLE_WIDGET });
    }
    
    dispatch({ type: ACTIONS.UPDATE_WIDGET_POSITION, payload: bubblePosition });
  };

  const handlePopoutMouseDown = (e) => {
    if (e.button !== 0 || isResizing) return;
    
    const rect = popoutRef.current.getBoundingClientRect();
    setPopoutDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsPopoutDragging(true);
    e.preventDefault();
  };

  const handleResizeMouseDown = (e, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStartData({
      x: e.clientX,
      y: e.clientY,
      width: popoutSize.width,
      height: popoutSize.height
    });
    e.preventDefault();
  };

  const handlePopoutMouseMove = useCallback((e) => {
    if (isResizing) {
      const deltaX = e.clientX - resizeStartData.x;
      const deltaY = e.clientY - resizeStartData.y;
      
      let newWidth = resizeStartData.width;
      let newHeight = resizeStartData.height;
      
      if (resizeHandle.includes('right')) {
        newWidth = Math.max(POPUP_MIN_WIDTH, Math.min(POPUP_MAX_WIDTH, resizeStartData.width + deltaX));
      }
      if (resizeHandle.includes('left')) {
        newWidth = Math.max(POPUP_MIN_WIDTH, Math.min(POPUP_MAX_WIDTH, resizeStartData.width - deltaX));
      }
      if (resizeHandle.includes('bottom')) {
        newHeight = Math.max(POPUP_MIN_HEIGHT, Math.min(POPUP_MAX_HEIGHT, resizeStartData.height + deltaY));
      }
      if (resizeHandle.includes('top')) {
        newHeight = Math.max(POPUP_MIN_HEIGHT, Math.min(POPUP_MAX_HEIGHT, resizeStartData.height - deltaY));
      }
      
      setPopoutSize({ width: newWidth, height: newHeight });
      
      // Update transcript panel position if it's open
      if (transcriptPanelRef.current && transcriptState.isOpen) {
        transcriptPanelRef.current.style.height = `${newHeight}px`;
        
        const spaceOnRight = window.innerWidth - (popoutPosition.x + newWidth + WIDGET_PADDING + TRANSCRIPT_WIDTH);
        let transcriptX;
        if (spaceOnRight > 0) {
          transcriptX = popoutPosition.x + newWidth + WIDGET_PADDING;
        } else {
          transcriptX = popoutPosition.x - TRANSCRIPT_WIDTH - WIDGET_PADDING;
        }
        transcriptPanelRef.current.style.left = `${transcriptX}px`;
      }
      
      return;
    }

    if (!isPopoutDragging) return;

    const newX = e.clientX - popoutDragOffset.x;
    const newY = e.clientY - popoutDragOffset.y;

    const maxX = window.innerWidth - popoutSize.width;
    const maxY = window.innerHeight - popoutSize.height;
    
    const clampedX = Math.max(0, Math.min(newX, maxX));
    const clampedY = Math.max(0, Math.min(newY, maxY));

    setPopoutPosition({
      x: clampedX,
      y: clampedY,
    });
    
    if (transcriptPanelRef.current && transcriptState.isOpen) {
      transcriptPanelRef.current.style.top = `${clampedY}px`;
      
      const spaceOnRight = window.innerWidth - (clampedX + popoutSize.width + WIDGET_PADDING + TRANSCRIPT_WIDTH);
      let transcriptX;
      if (spaceOnRight > 0) {
        transcriptX = clampedX + popoutSize.width + WIDGET_PADDING;
      } else {
        transcriptX = clampedX - TRANSCRIPT_WIDTH - WIDGET_PADDING;
      }
      transcriptPanelRef.current.style.left = `${transcriptX}px`;
    }
  }, [isResizing, isPopoutDragging, resizeHandle, resizeStartData, popoutDragOffset, transcriptState.isOpen, popoutPosition, popoutSize]);

  const handlePopoutMouseUp = useCallback(() => {
    setIsPopoutDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  useEffect(() => {
    if (isBubbleDragging) {
      document.addEventListener('mousemove', handleBubbleMouseMove);
      document.addEventListener('mouseup', handleBubbleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleBubbleMouseMove);
      document.removeEventListener('mouseup', handleBubbleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleBubbleMouseMove);
      document.removeEventListener('mouseup', handleBubbleMouseUp);
    };
  }, [isBubbleDragging, bubbleDragOffset, bubblePosition]);

  useEffect(() => {
    if (isPopoutDragging || isResizing) {
      if (transcriptPanelRef.current) {
        transcriptPanelRef.current.style.transition = 'none';
      }
      document.addEventListener('mousemove', handlePopoutMouseMove);
      document.addEventListener('mouseup', handlePopoutMouseUp);
    } else {
      if (transcriptPanelRef.current) {
        transcriptPanelRef.current.style.transition = 'left 0.15s ease-in-out';
      }
      document.removeEventListener('mousemove', handlePopoutMouseMove);
      document.removeEventListener('mouseup', handlePopoutMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handlePopoutMouseMove);
      document.removeEventListener('mouseup', handlePopoutMouseUp);
    };
  }, [isPopoutDragging, isResizing, handlePopoutMouseMove, handlePopoutMouseUp]);

  useEffect(() => {
    if (isOpen) {
      // Calculate desired position above the bubble
      const idealX = bubblePosition.x - (popoutSize.width / 2) + (BUBBLE_SIZE / 2);
      const idealY = bubblePosition.y - popoutSize.height - WIDGET_PADDING;

      // Clamp position to be within viewport bounds
      const x = Math.max(
        WIDGET_PADDING,
        Math.min(idealX, window.innerWidth - popoutSize.width - WIDGET_PADDING)
      );
      const y = Math.max(
        WIDGET_PADDING,
        Math.min(idealY, window.innerHeight - popoutSize.height - WIDGET_PADDING)
      );
      
      setPopoutPosition({ x, y });
    }
  }, [isOpen, bubblePosition, popoutSize]);

  const transcriptPanelStyle = {
    position: 'fixed',
    top: popoutPosition.y,
    width: TRANSCRIPT_WIDTH,
    height: popoutSize.height,
    zIndex: 1050,
    transition: 'left 0.15s ease-in-out',
  };

  const spaceOnRight = window.innerWidth - (popoutPosition.x + popoutSize.width + WIDGET_PADDING + TRANSCRIPT_WIDTH);
  if (spaceOnRight > 0) {
    transcriptPanelStyle.left = popoutPosition.x + popoutSize.width + WIDGET_PADDING;
  } else {
    transcriptPanelStyle.left = popoutPosition.x - TRANSCRIPT_WIDTH - WIDGET_PADDING;
  }

  const bubble = (
    <div
      ref={bubbleRef}
      style={{
        position: 'fixed',
        left: bubblePosition.x,
        top: bubblePosition.y,
        touchAction: 'none',
      }}
      className={`z-1000 transform transition-transform duration-200 ${isBubbleDragging ? 'scale-110' : 'scale-100'}`}
      onMouseDown={handleBubbleMouseDown}
    >
      <div 
        className="relative w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center shadow-lg cursor-pointer"
      >
        <div style={{ transform: 'translateX(0px)' }}>
          <GetTalkyLogo style={{ width: 36, height: 36, fill: 'white' }} />
        </div>

        {pendingInteractions.length > 0 && !isOpen && (
          <span className={`absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold shadow-sm ${
            pendingInteractions.length > 9 ? 'px-1.5 h-5' : 'h-5 w-5'
          }`}>
            {pendingInteractions.length}
          </span>
        )}
        
        {pendingInteractions.length > 0 && !isOpen && !isBubbleDragging && (
          <div className="absolute inset-0 rounded-full animate-ping-slow opacity-75" style={{ background: 'red' }}></div>
        )}
      </div>
    </div>
  );

  if (!isOpen) {
    return bubble;
  }

  // Render widget content based on loading state
  let widgetContent;
  if (isLoading) {
    widgetContent = <LoadingView />;
  } else if (error) {
    widgetContent = <ErrorView error={error} onRetry={handleRetry} />;
  } else if (view === 'callQueue') {
    widgetContent = <CallQueueView />;
  } else {
    widgetContent = <ConversationView />;
  }

  return (
    <>
      {bubble}
      <div
        ref={popoutRef}
        style={{
          position: 'fixed',
          left: popoutPosition.x,
          top: popoutPosition.y,
          width: popoutSize.width,
          height: popoutSize.height,
          touchAction: 'none',
        }}
        className="z-1001 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden select-none"
      >
        {/* Widget header */}
        <div 
          className="bg-gradient-to-r from-primary-500 to-primary-600 p-3 flex items-center justify-between cursor-move"
          onMouseDown={handlePopoutMouseDown}
        >
          <div className="flex items-center space-x-2">
            <GetTalkyLogo style={{ width: 20, height: 20, fill: 'white' }} />
            <h3 className="text-white font-semibold text-sm">Get Talky Agent</h3>
          </div>
          <button
            onClick={() => dispatch({ type: ACTIONS.TOGGLE_WIDGET })}
            className="text-white hover:text-gray-200 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Widget content */}
        <div className="h-full pb-12">
          {widgetContent}
        </div>

        {/* Resize handles */}
        <div
          className="absolute top-0 right-0 w-4 h-4 cursor-ne-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'top-right')}
        />
        <div
          className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'top-left')}
        />
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
        />
        <div
          className="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
        />
        <div
          className="absolute top-0 left-4 right-4 h-2 cursor-n-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
        />
        <div
          className="absolute bottom-0 left-4 right-4 h-2 cursor-s-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
        />
        <div
          className="absolute top-4 bottom-4 left-0 w-2 cursor-w-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
        />
        <div
          className="absolute top-4 bottom-4 right-0 w-2 cursor-e-resize"
          onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
        />
      </div>

      {/* Transcript Panel */}
      {transcriptState.isOpen && (
        <div ref={transcriptPanelRef} style={transcriptPanelStyle}>
          <TranscriptPanel />
        </div>
      )}
    </>
  );
};

export default AssistantWidget; 